import { useCallback, useEffect, useRef, useState } from "react";
import { markItemDcRoutingPacking } from "@services/api";
import { notifySuccess, notifyError } from "@components/ds/notify";
import { ISSUE_IMAGE_BUCKETS, USE_FIXTURES } from "../constants";
import { itemsQuantityStatusV3Fixture } from "../__fixtures__/fixtureStore";

// Wraps POST /api/leader/dc-routing-packing/v1/items/:order_item_id/marking for
// both the MARKING (submit) and REMOVE_MARKING (negative diff) flows.
// Keeps a list of currently-processing order_item_ids so the parent can
// disable rows while a request is in flight (mirrors
// processingOrderItemIds in the web's data()).
//
// Real image URLs flow in via the `images` arg from MarkingSheet. The
// builder slots them into the buckets the server expects; reasons without
// uploaded images get an empty array (server rejects damaged / wrong-item
// without ≥1 URL — sheet enforces that before submit).

const buildIssueImages = (diff, images = {}) => {
  const out = {};
  ISSUE_IMAGE_BUCKETS.forEach((k) => {
    out[k] = [];
  });
  Object.keys(diff || {}).forEach((reason) => {
    if (!ISSUE_IMAGE_BUCKETS.includes(reason)) return;
    if (Number(diff[reason]) <= 0) return;
    const urls = Array.isArray(images[reason]) ? images[reason] : [];
    out[reason] = urls.filter(Boolean);
  });
  return out;
};

const useMarking = ({ reload } = {}) => {
  const [processing, setProcessing] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const safeSetProcessing = (updater) => {
    if (isMounted.current) setProcessing(updater);
  };

  const isProcessing = useCallback(
    (orderItemId) => processing.includes(Number(orderItemId)),
    [processing],
  );

  const trackStart = useCallback(
    (id) =>
      safeSetProcessing((prev) =>
        prev.includes(Number(id)) ? prev : [...prev, Number(id)],
      ),
    [],
  );
  const trackEnd = useCallback(
    (id) => safeSetProcessing((prev) => prev.filter((x) => x !== Number(id))),
    [],
  );

  const submitMarking = useCallback(
    async ({ item, diff, images = {} }) => {
      if (!item?.order_item_id) return false;
      const id = Number(item.order_item_id);
      setSubmitting(true);
      trackStart(id);
      try {
        const payload = {
          order_item_id: item.order_item_id,
          diff: { ...diff },
          current_state: item.current_state || {},
          hash: item.hash || "",
          type: "MARKING",
          ofd_type: "PRE_OFD",
          noDiff: false,
          issue_images: buildIssueImages(diff, images),
        };
        if (USE_FIXTURES) {
          itemsQuantityStatusV3Fixture(payload);
        } else {
          await markItemDcRoutingPacking(payload);
        }
        notifySuccess("Item marked");
        if (reload) await reload();
        return true;
      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to mark item";
        notifyError(msg);
        return false;
      } finally {
        if (isMounted.current) setSubmitting(false);
        trackEnd(id);
      }
    },
    [reload, trackStart, trackEnd],
  );

  const removeMarking = useCallback(
    async (item) => {
      if (!item?.order_item_id || !item?.issue_reason) return false;
      const id = Number(item.order_item_id);
      setSubmitting(true);
      trackStart(id);
      try {
        const payload = {
          order_item_id: item.order_item_id,
          diff: { [item.issue_reason]: -Number(item.quantity || 0) },
          current_state: item.current_state || {},
          hash: item.hash || "",
          type: "MARKING",
          ofd_type: "PRE_OFD",
          noDiff: false,
        };
        if (USE_FIXTURES) {
          itemsQuantityStatusV3Fixture(payload);
        } else {
          await markItemDcRoutingPacking(payload);
        }
        notifySuccess("Marking removed");
        if (reload) await reload();
        return true;
      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to remove marking";
        notifyError(msg);
        return false;
      } finally {
        if (isMounted.current) setSubmitting(false);
        trackEnd(id);
      }
    },
    [reload, trackStart, trackEnd],
  );

  return {
    submitMarking,
    removeMarking,
    submitting,
    processing,
    isProcessing,
  };
};

export default useMarking;
